import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Loader2, CheckCircle2, Phone, Mail, MapPin, Send, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0A0A0A]" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">
            Get in Touch
          </p>
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="contact-title"
          >
            {t('contact_title')}
          </h1>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            {t('contact_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Address */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="contact-address-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] text-[#D4AF37]">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">{t('contact_address')}</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Bahnhofstrasse 1<br />
                    8560 Marstetten<br />
                    Switzerland
                  </p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="contact-phone-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] text-[#D4AF37]">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">{t('contact_phone')}</h3>
                  <a 
                    href="tel:+41799270731" 
                    className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors"
                  >
                    +41 79 927 07 31
                  </a>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="contact-email-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] text-[#D4AF37]">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">{t('contact_email')}</h3>
                  <a 
                    href="mailto:etourtransfer@gmail.com" 
                    className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors"
                  >
                    etourtransfer@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="bg-[#121212] border border-green-500/30 p-6" data-testid="contact-whatsapp-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-green-500 text-green-500">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">WhatsApp</h3>
                  <a 
                    href="https://wa.me/41799270731" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#A1A1AA] text-sm hover:text-green-500 transition-colors"
                  >
                    +41 79 927 07 31
                  </a>
                  <p className="text-xs text-green-500/70 mt-1">Click to chat directly</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 bg-[#121212] border border-white/10 p-8">
            <h2 
              className="text-2xl text-white mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('contact_form_title')}
            </h2>

            {success ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">{t('contact_success')}</h3>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-4 px-6 py-2 border border-white/20 text-white hover:bg-white/5 transition-all duration-300"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {t('booking_name')} *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                      required
                      data-testid="contact-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {t('booking_email')} *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                      required
                      data-testid="contact-email"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {t('booking_phone')} *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                      required
                      data-testid="contact-phone"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {t('contact_subject')} *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                      required
                      data-testid="contact-subject"
                    />
                  </div>

                  {/* Message */}
                  <div className="md:col-span-2">
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {t('contact_message')} *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none resize-none"
                      required
                      data-testid="contact-message"
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-contact-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('contact_send')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
